/**
 * Visibility chips for a document — the ACL-derived audience rendered as
 * pills: "Organisation" (org-wide grant) and one chip per role and unit
 * share; the share dialog adds amber "pending approval" chips for the
 * caller's open publish requests (PendingPublishChips). When the
 * grant set is empty the label depends on WHO is looking: "Private" for the
 * owner, "Shared with you" for anyone else — a private document you can read
 * but don't own IS a direct share, and labelling it "Private" is the exact
 * mental-model contradiction this component exists to remove.
 *
 * Shared by the document list's read-only rows and the detail page; the
 * list's owner-editable cell composes the same chip helpers around its
 * org-wide <select>.
 */

import { useTranslation } from 'react-i18next';
import type { AccessRequest } from '@/api/endpoints/accessRequests';
import type { Document } from '@/api/endpoints/documents';

// A single granted-audience chip (role or unit name). `keyPrefix` namespaces
// the React key so a role and a unit sharing a display name can't collide.
const audienceChip = (keyPrefix: string) =>
  function chip(name: string) {
    return (
      <span
        key={`${keyPrefix}:${name}`}
        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700"
      >
        {name}
      </span>
    );
  };
export const roleChip = audienceChip('role');
export const unitChip = audienceChip('unit');

/**
 * One amber chip per OPEN audience-expansion request: "Organisation —
 * pending approval" for a visibility request, "<role/unit name> — pending
 * approval" for a grant request. In-flight — neither granted nor refused.
 */
export function PendingPublishChips({ pending }: { pending: AccessRequest[] }) {
  const { t } = useTranslation();
  return (
    <>
      {pending.map((p) => (
        <span
          key={p.id}
          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
        >
          {t('documents.list.visibility.pendingApproval', {
            name:
              p.change === 'visibility'
                ? t('documents.list.visibility.organisation', 'Organisation')
                : p.grantee_name || p.grantee_type || '',
            defaultValue: '{{name}} — pending approval',
          })}
        </span>
      ))}
    </>
  );
}

interface VisibilityChipsProps {
  doc: Document;
  /** The viewing user's id — decides "Private" (own doc) vs "Shared with you". */
  viewerId?: string;
}

export function VisibilityChips({ doc, viewerId }: VisibilityChipsProps) {
  const { t } = useTranslation();
  // `?? {}` defaults: the backend always populates visibility, but never
  // crash a clinical surface on a missing field (frontend resilience rule).
  const { org_wide: orgWide = false, roles = [], units = [] } = doc.visibility ?? {};

  return (
    <div className="flex flex-wrap items-center gap-1">
      {orgWide && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
          {t('documents.list.visibility.organisation', 'Organisation')}
        </span>
      )}
      {roles.map(roleChip)}
      {units.map(unitChip)}
      {/* viewerId != null: before the auth store rehydrates the viewer is
          unknown and "Private" vs "Shared with you" would be a guess. The
          org/role/unit chips above don't depend on who is looking, so only
          this fallback chip waits. */}
      {!orgWide &&
        roles.length === 0 &&
        units.length === 0 &&
        viewerId != null && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-600">
            {doc.owner_id !== viewerId
              ? t('documents.list.visibility.sharedWithYou', 'Shared with you')
              : t('documents.list.visibility.private', 'Private')}
          </span>
        )}
    </div>
  );
}
